import React from "react";
import { Link } from "react-router-dom";

import type { IServicePageContent } from "../components/pages/service-page";

/**
 * Service landing pages. Each page targets a specific service+intent
 * query (e.g. "massaggio svedese firenze"). The shared ServicePage
 * component renders each one with page-specific schema (Service + FAQ).
 *
 * Copy is gender-neutral (no gender-bound adjectives directed at the
 * reader). Duration hints feed into Service schema `eligibleDuration`.
 */

export const svedeseContent: IServicePageContent = {
  slug: "massaggio-svedese",
  displayName: "Massaggio Svedese",
  title: "Massaggio Svedese a Firenze · Laviens Studio Olistico",
  description:
    "Massaggio svedese a Firenze: il massaggio classico rilassante, a tocco fluido. 60 o 90 minuti con Regina — studio olistico a Sesto Fiorentino. Prenota con una telefonata.",
  keywords:
    "massaggio svedese firenze, massaggio svedese sesto fiorentino, massaggio rilassante firenze, massaggio classico firenze",
  serviceType: "Swedish Massage",
  durationISO: "PT60M",
  durationHuman: "60 o 90 minuti",
  heroImage: "/heroes/massaggio-svedese.jpg",
  heroAlt:
    "Massaggio svedese rilassante nello studio olistico Laviens a Sesto Fiorentino",
  eyebrow: "Massaggi olistici",
  heroTitle: "Svedese.",
  heroSubtitle: "Il massaggio classico rilassante — un'onda continua, mai la stessa.",
  lead: (
    <>
      Il <em>massaggio svedese</em> è il massaggio classico rilassante:
      movimenti fluidi, pressione media, tocco continuo. È il punto di
      partenza ideale per chi vuole staccare davvero — senza ricerca di
      contratture, senza pretese tecniche, solo un'ora in cui il sistema
      nervoso rallenta.
    </>
  ),
  whoFor: [
    "Chi ha bisogno di staccare a fine settimana o dopo un periodo intenso.",
    "Chi vuole scaricare stress generico, senza zone particolarmente doloranti.",
    "Chi è alla prima esperienza e vuole iniziare con un massaggio gentile.",
    "Chi cerca un gesto di cura regolare (cadenza consigliata: ogni 2–3 settimane).",
  ],
  howItWorks: (
    <>
      <p>
        La seduta dura <strong>60 o 90 minuti</strong>. Si inizia con una
        breve chiacchierata — come stai, se ci sono zone particolarmente
        tese, quanto tempo vuoi dedicarti. Poi si lavora in sequenza
        sulle grandi aree del corpo: schiena, gambe, braccia, nuca,
        viso. La pressione è media, costante, e si può modulare in
        qualsiasi momento.
      </p>
      <p>
        A differenza del <Link to="/massaggio-decontratturante">decontratturante</Link>, lo svedese non
        affonda nelle contratture. Resta in superficie, attiva la
        circolazione, aiuta il corpo a lasciare andare. Per chi non
        saprebbe scegliere, la nostra{" "}
        <Link to="/journal/svedese-o-decontratturante">guida dedicata</Link>{" "}
        aiuta a decidere.
      </p>
    </>
  ),
  faq: [
    {
      q: "Il massaggio svedese è intenso?",
      a: "No. È un massaggio rilassante, non terapeutico. Se cerchi pressione profonda per sciogliere contratture, meglio il decontratturante.",
    },
    {
      q: "Per un primo massaggio, va bene lo svedese?",
      a: "Sì, è la scelta consigliata per chi non ha mai ricevuto un massaggio professionale: pressione media, ritmo lento, massima calma.",
    },
    {
      q: "Quanto dura una seduta?",
      a: "60 o 90 minuti. Concordiamo in telefonata in base a quanto tempo vuoi dedicarti.",
    },
    {
      q: "Con quale frequenza conviene farlo?",
      a: "Per mantenimento, ogni 2–3 settimane. In periodi di stress intenso anche settimanalmente.",
    },
  ],
  relatedServices: [
    "massaggio-decontratturante",
    "aromaterapia",
    "massaggio-californiano",
  ],
  relatedArticles: ["svedese-o-decontratturante", "prima-volta-studio-olistico"],
};

export const decontratturanteContent: IServicePageContent = {
  slug: "massaggio-decontratturante",
  displayName: "Massaggio Decontratturante",
  title: "Massaggio Decontratturante a Firenze · Cervicale, Lombare, Trapezi",
  description:
    "Massaggio decontratturante a Firenze: mirato a cervicale, trapezi e lombari. Ideale per chi lavora al computer o ha tensioni croniche. 60 o 90 minuti con Regina a Sesto Fiorentino.",
  keywords:
    "massaggio decontratturante firenze, massaggio cervicale firenze, massaggio lombare firenze, massaggio trapezi firenze, contrattura cervicale massaggio, dolore schiena massaggio firenze",
  serviceType: "Deep Tissue Massage",
  durationISO: "PT60M",
  durationHuman: "60 o 90 minuti",
  heroImage: "/heroes/massaggio-decontratturante.jpg",
  heroAlt:
    "Massaggio decontratturante per cervicale e lombare nello studio olistico Laviens",
  eyebrow: "Massaggi terapeutici",
  heroTitle: "Decontratturante.",
  heroSubtitle: "Tensioni cervicali, lombari, trapezi — sciolte una per volta.",
  lead: (
    <>
      Il <em>massaggio decontratturante</em> è un lavoro terapeutico:
      pressione più profonda, manovre mirate sulle contratture — quei
      nodi di muscolo che restano tesi anche quando la tensione sembra
      andata via. Non è un massaggio solo rilassante, ma è un
      massaggio che lascia sollievo vero.
    </>
  ),
  whoFor: [
    "Chi lavora molte ore al computer e ha cervicale e trapezi rigidi.",
    "Chi ha dolori lombari ricorrenti o mal di testa tensivi.",
    "Chi guida tanto o passa molte ore in piedi.",
    "Chi ha completato un periodo di stress fisico (trasloco, viaggio, maratona).",
    "Chi vuole un risultato mirato, non solo un'ora di relax.",
  ],
  howItWorks: (
    <>
      <p>
        La seduta dura <strong>60 o 90 minuti</strong>. Si parte da una
        valutazione: dove senti tensione, da quanto tempo, come si
        manifesta. Poi si lavora sulla zona con manovre specifiche —
        pressione profonda, sblocco miofasciale, scarico della catena
        muscolare coinvolta.
      </p>
      <p>
        La pressione può essere intensa dove trova una contrattura, ma
        mai insopportabile. Si calibra continuamente sul tuo feedback. Se
        una zona è troppo dolorante, si riduce e si torna con un approccio
        diverso.
      </p>
      <p>
        Spesso, nei casi di tensione cronica, conviene combinare il
        decontratturante con il{" "}
        <Link to="/massaggio-svedese">massaggio svedese</Link> nella stessa
        seduta: 20–30 minuti di lavoro profondo sulle contratture, poi
        rilassamento totale.
      </p>
    </>
  ),
  notFor: [
    "Gravidanza (a meno di indicazione medica specifica).",
    "Infiammazioni acute, ematomi recenti, febbre.",
    "Problemi cardiaci scompensati o trombosi in corso.",
    "Dopo un intervento chirurgico recente, senza il via libera del medico.",
  ],
  faq: [
    {
      q: "Fa male?",
      a: "Può essere intenso dove c'è una contrattura, ma mai insopportabile. Regina calibra la pressione in base al tuo feedback — si lavora con te, non contro di te.",
    },
    {
      q: "Quanto dura l'effetto?",
      a: "Per una contrattura acuta, una seduta può dare sollievo immediato che dura giorni. Per tensioni croniche conviene un ciclo di 3–5 sedute ravvicinate.",
    },
    {
      q: "Serve la prescrizione medica?",
      a: "No, è un massaggio benessere, non una prestazione sanitaria. Se però hai una patologia in corso, parlane prima con il tuo medico.",
    },
    {
      q: "Lo svedese o il decontratturante?",
      a: "Decontratturante se hai zone specifiche dolorose o tensioni croniche. Svedese se cerchi solo rilassamento. Al telefono capiamo insieme quale sceglier (o miscelare).",
    },
  ],
  relatedServices: [
    "massaggio-svedese",
    "massaggio-linfodrenante",
    "aromaterapia",
  ],
  relatedArticles: ["svedese-o-decontratturante", "prima-volta-studio-olistico"],
};

export const linfodrenanteContent: IServicePageContent = {
  slug: "massaggio-linfodrenante",
  displayName: "Massaggio Linfodrenante",
  title:
    "Massaggio Linfodrenante a Firenze · Metodo Vodder · Gambe Leggere",
  description:
    "Linfodrenaggio manuale (Metodo Vodder) a Firenze: drena ritenzione idrica, gonfiore e gambe pesanti. 60 minuti con Regina a Sesto Fiorentino. Ideale in estate e dopo voli lunghi.",
  keywords:
    "linfodrenaggio firenze, massaggio linfodrenante firenze, metodo vodder firenze, gambe pesanti rimedio, ritenzione idrica massaggio, drenaggio linfatico sesto fiorentino",
  serviceType: "Lymphatic Drainage Massage",
  durationISO: "PT60M",
  durationHuman: "60 minuti",
  heroImage: "/heroes/massaggio-linfodrenante.jpg",
  heroAlt:
    "Massaggio linfodrenante metodo Vodder nello studio olistico Laviens",
  eyebrow: "Massaggi specifici",
  heroTitle: "Linfodrenaggio.",
  heroSubtitle: "Manovre leggere, risultato misurabile.",
  lead: (
    <>
      Il <em>linfodrenaggio manuale</em> (Metodo Vodder) è una tecnica
      specifica: movimenti leggeri, lenti, ritmici, che seguono la
      direzione del flusso linfatico. Non è un massaggio rilassante
      classico e non è un anticellulite: è un lavoro sul sistema
      linfatico per drenare liquidi in eccesso.
    </>
  ),
  whoFor: [
    "Chi ha gambe pesanti, caviglie gonfie a fine giornata.",
    "Chi è reduce da voli lunghi o giornate di sedentarietà.",
    "Chi ha ritenzione idrica ciclica o stagionale.",
    "Chi segue un percorso post-operatorio (con ok del medico).",
    "Chi vuole mantenere il microcircolo attivo durante l'estate.",
  ],
  howItWorks: (
    <>
      <p>
        La seduta dura <strong>60 minuti</strong>. La pressione è molto
        leggera — quasi sorprendente, se non hai mai provato. I movimenti
        seguono la direzione del flusso linfatico: dagli arti verso i
        nodi linfatici (ascellari, inguinali, addominali).
      </p>
      <p>
        Si lavora spesso su gambe, addome, braccia e viso. Dopo la seduta
        è <strong>normale avere bisogno del bagno</strong> nelle ore
        successive — è un buon segno, significa che il sistema si è
        riattivato.
      </p>
      <p>
        Per una sola seduta, le gambe sono più leggere per 2–5 giorni.
        Per risultati stabili (soprattutto in caso di ritenzione cronica),
        si consigliano cicli di 4–8 sedute ravvicinate, seguite da
        sedute di mantenimento.
      </p>
    </>
  ),
  notFor: [
    "Infezioni acute o stati febbrili.",
    "Scompenso cardiaco acuto.",
    "Trombosi venosa profonda in corso.",
    "Tumori in fase attiva senza indicazione oncologica.",
  ],
  faq: [
    {
      q: "Il linfodrenaggio è rilassante?",
      a: "Non nel senso classico. La pressione è leggera e ritmica, più che rilassante è attivante: il sistema linfatico si riattiva.",
    },
    {
      q: "Serve contro la cellulite?",
      a: "Il linfodrenaggio non è un massaggio anticellulite in senso stretto. Aiuta sul versante drenaggio, ma per la cellulite si preferisce il massaggio anticellulite modellante.",
    },
    {
      q: "Quante sedute servono?",
      a: "Per un evento specifico (viaggio, matrimonio, caldo estivo): 1–2 sedute. Per ritenzione cronica: ciclo di 4–8 ravvicinate + mantenimento mensile.",
    },
    {
      q: "Posso farlo in gravidanza?",
      a: "In alcuni casi sì, ma solo su indicazione e autorizzazione del ginecologo. Parlane con noi e con il tuo medico prima di prenotare.",
    },
    {
      q: "Per saperne di più?",
      a: "Abbiamo scritto una guida completa sul linfodrenaggio — cosa è, quando serve, quando non è indicato.",
    },
  ],
  relatedServices: [
    "massaggio-decontratturante",
    "massaggio-svedese",
    "aromaterapia",
  ],
  relatedArticles: ["cose-il-linfodrenaggio", "prima-volta-studio-olistico"],
};

export const ayurvedicoContent: IServicePageContent = {
  slug: "massaggio-ayurvedico",
  displayName: "Massaggio Ayurvedico",
  title: "Massaggio Ayurvedico a Firenze · Olii Caldi, Tradizione Indiana",
  description:
    "Massaggio ayurvedico a Firenze: olii caldi e manovre lente della tradizione indiana per riequilibrare corpo e mente. 90 minuti con Regina a Sesto Fiorentino.",
  keywords:
    "massaggio ayurvedico firenze, massaggio ayurveda sesto fiorentino, abhyanga firenze, massaggio olii caldi firenze, trattamento ayurvedico toscana",
  serviceType: "Ayurvedic Massage",
  durationISO: "PT90M",
  durationHuman: "90 minuti",
  heroImage: "/heroes/massaggio-ayurvedico.jpg",
  heroAlt:
    "Massaggio ayurvedico con olii caldi nello studio olistico Laviens a Sesto Fiorentino",
  eyebrow: "Massaggi tradizionali",
  heroTitle: "Ayurvedico.",
  heroSubtitle: "Olii caldi, manovre lente, un'ora e mezza di profondità.",
  lead: (
    <>
      Il <em>massaggio ayurvedico</em> nasce dalla tradizione millenaria
      indiana. Usa olii caldi, manovre lente e un ritmo costante per
      lavorare su corpo e mente insieme. È un'esperienza di connessione
      profonda più che un semplice trattamento.
    </>
  ),
  whoFor: [
    "Chi cerca un'esperienza di massaggio profonda, non solo tecnica.",
    "Chi vuole lavorare anche sul piano emotivo, non solo muscolare.",
    "Chi ha 90 minuti da dedicarsi davvero.",
    "Chi ama il profumo degli olii naturali e la calma del ritmo lento.",
    "Chi è in un periodo di passaggio (cambi di stagione, transizioni di vita).",
  ],
  howItWorks: (
    <>
      <p>
        La seduta dura <strong>90 minuti</strong>. Si inizia con la scelta
        dell'olio in base al tuo momento — più caldo, più neutro, più
        avvolgente. L'olio viene scaldato e applicato in modo generoso.
        Le manovre sono <strong>lente, ampie, costanti</strong>: seguono
        la direzione dei meridiani e dei chakra secondo la tradizione
        ayurvedica.
      </p>
      <p>
        A differenza del <Link to="/massaggio-svedese">massaggio svedese</Link>,
        l'ayurvedico non è solo superficiale: usa il ritmo per entrare
        più in profondità senza pressione aggressiva. È adatto anche a
        chi trova il decontratturante "troppo diretto".
      </p>
      <p>
        Dopo la seduta, è consigliato lasciare l'olio in pelle per un
        po' — pianifica di tornare a casa senza appuntamenti
        immediatamente successivi.
      </p>
    </>
  ),
  faq: [
    {
      q: "Devo sapere qualcosa di ayurveda prima di venire?",
      a: "No. Al telefono capiamo insieme cosa stai cercando e scegliamo l'olio e il ritmo giusti per te. Non serve conoscenza pregressa.",
    },
    {
      q: "Resto con l'olio addosso?",
      a: "Sì, è parte del trattamento. L'olio continua a lavorare sulla pelle per qualche ora. Vieni con abiti comodi, scuri se possibile.",
    },
    {
      q: "Quanto spesso si può fare?",
      a: "Tradizionalmente in cicli (3–5 sedute ravvicinate, ai cambi di stagione). Come trattamento occasionale è perfetto quando si sente il bisogno di rallentare.",
    },
    {
      q: "È rilassante o energizzante?",
      a: "Dipende dall'olio scelto e dal ritmo. Tendenzialmente profondamente rilassante per chi è in sovraccarico; radicante per chi è disperso.",
    },
  ],
  relatedServices: [
    "massaggio-svedese",
    "aromaterapia",
    "massaggio-californiano",
  ],
  relatedArticles: ["prima-volta-studio-olistico"],
};

export const californianoContent: IServicePageContent = {
  slug: "massaggio-californiano",
  displayName: "Massaggio Californiano",
  title: "Massaggio Californiano a Firenze · Avvolgente ed Emozionale",
  description:
    "Massaggio californiano a Firenze: manovre lunghe e continue, avvolgente ed emozionale. 90 minuti di rilassamento profondo con Regina a Sesto Fiorentino.",
  keywords:
    "massaggio californiano firenze, massaggio emozionale firenze, massaggio avvolgente firenze, rilassamento profondo firenze",
  serviceType: "Californian Massage",
  durationISO: "PT90M",
  durationHuman: "90 minuti",
  heroImage: "/heroes/massaggio-californiano.jpg",
  heroAlt:
    "Massaggio californiano avvolgente nello studio olistico Laviens",
  eyebrow: "Massaggi emozionali",
  heroTitle: "Californiano.",
  heroSubtitle: "Un'onda lenta — dalle caviglie alla nuca, senza mai staccare.",
  lead: (
    <>
      Il <em>massaggio californiano</em> è un massaggio avvolgente ed
      emozionale: manovre lunghe, lente, continue, che percorrono tutto
      il corpo come un'unica onda. È il massaggio per chi ha bisogno di
      essere "tenuto" più che manipolato.
    </>
  ),
  whoFor: [
    "Chi attraversa un periodo di grande carico emotivo o stress mentale.",
    "Chi ha bisogno di rallentare davvero, non solo riposare.",
    "Chi cerca un massaggio più intenso emotivamente che fisicamente.",
    "Chi ha trovato poco efficaci i massaggi più tecnici.",
    "Chi ha 90 minuti e vuole che siano un'esperienza completa.",
  ],
  howItWorks: (
    <>
      <p>
        La seduta dura <strong>90 minuti</strong>. Si lavora su tutto il
        corpo con manovre ampie che non si interrompono mai: il tocco
        resta sempre in contatto, passa da una zona all'altra senza
        stacchi. Questo crea un effetto <em>contenitivo</em> che permette
        al sistema nervoso di abbandonare.
      </p>
      <p>
        Rispetto al <Link to="/massaggio-ayurvedico">massaggio ayurvedico</Link>,
        il californiano lavora meno sui meridiani e più sulla relazione
        corpo-respiro. Spesso ci si addormenta, e va bene così.
      </p>
    </>
  ),
  faq: [
    {
      q: "È terapeutico?",
      a: "Non nel senso di lavorare su contratture. È un massaggio rilassante profondo, con effetto soprattutto sul sistema nervoso.",
    },
    {
      q: "Va bene se non amo il contatto prolungato?",
      a: "Se il tocco continuo ti mette a disagio, meglio un massaggio più tecnico. Parliamone in telefonata.",
    },
    {
      q: "Quanto dura l'effetto?",
      a: "Sul piano fisico qualche giorno; sul piano della calma mentale, spesso di più — soprattutto se inserito in un ciclo.",
    },
    {
      q: "Posso abbinarlo all'aromaterapia?",
      a: "Sì, è un'ottima combinazione. Olii essenziali calmi (lavanda, camomilla, ylang-ylang) amplificano l'effetto contenitivo.",
    },
  ],
  relatedServices: [
    "massaggio-ayurvedico",
    "massaggio-svedese",
    "aromaterapia",
  ],
  relatedArticles: ["prima-volta-studio-olistico"],
};

export const hawaianoContent: IServicePageContent = {
  slug: "massaggio-hawaiano",
  displayName: "Massaggio Hawaiano",
  title: "Massaggio Hawaiano a Firenze · Lomi Lomi, Onde di Olio Caldo",
  description:
    "Massaggio hawaiano Lomi Lomi a Firenze: manovre lunghe e fluide come onde, con olio caldo. 60 minuti con Regina, studio olistico a Sesto Fiorentino. Prenota con una telefonata.",
  keywords:
    "massaggio hawaiano firenze, lomi lomi firenze, massaggio hawaiano sesto fiorentino, massaggio lomi lomi toscana",
  serviceType: "Hawaiian Massage",
  durationISO: "PT60M",
  durationHuman: "60 minuti",
  heroImage: "/heroes/massaggio-hawaiano.jpg",
  heroAlt:
    "Massaggio hawaiano Lomi Lomi con olio caldo nello studio olistico Laviens a Sesto Fiorentino",
  eyebrow: "Massaggi tradizionali",
  heroTitle: "Hawaiano.",
  heroSubtitle: "Lomi Lomi — un'onda continua di olio caldo, dalle spalle ai piedi.",
  lead: (
    <>
      Il <em>massaggio hawaiano Lomi Lomi</em> nasce dalla tradizione delle
      isole Hawaii: manovre lunghe, fluide e ritmiche, eseguite con
      avambracci e palme, che percorrono il corpo come le onde
      dell'oceano. Con olio caldo e un ritmo avvolgente, scioglie le
      tensioni e accompagna corpo e mente in un rilassamento profondo.
    </>
  ),
  whoFor: [
    "Chi cerca un massaggio fluido e avvolgente, più che tecnico.",
    "Chi vuole staccare davvero e lasciar andare lo stress accumulato.",
    "Chi ama il calore dell'olio e il ritmo continuo, senza stacchi.",
    "Chi desidera un'esperienza sensoriale completa di un'ora.",
    "Chi è in un periodo di transizione e vuole ritrovare equilibrio.",
  ],
  howItWorks: (
    <>
      <p>
        La seduta dura <strong>un'ora</strong>. Si inizia con una breve
        chiacchierata — come stai, dove senti più tensione — e con la
        scelta dell'olio, scaldato prima di iniziare.
        Poi si lavora su tutto il corpo con manovre <strong>lunghe,
        continue e ritmiche</strong>: avambracci e palme scorrono senza mai
        interrompere il contatto, come un'unica onda.
      </p>
      <p>
        A differenza del{" "}
        <Link to="/massaggio-decontratturante">decontratturante</Link>, il
        Lomi Lomi non affonda nelle contratture: usa il ritmo e il calore
        per far rilassare il sistema nervoso in profondità. Vicino al{" "}
        <Link to="/massaggio-californiano">californiano</Link> per
        l'effetto avvolgente, se ne distingue per l'uso generoso dell'olio
        caldo e per la fluidità delle manovre.
      </p>
      <p>
        Dopo la seduta è bello lasciare l'olio in pelle ancora un po':
        pianifica di tornare a casa con calma, senza impegni subito dopo.
      </p>
    </>
  ),
  faq: [
    {
      q: "Quanto dura una seduta?",
      a: "Un'ora. Solo su appuntamento — concordiamo l'orario in telefonata.",
    },
    {
      q: "È un massaggio rilassante o terapeutico?",
      a: "È un massaggio rilassante e avvolgente, non terapeutico. Per sciogliere contratture specifiche è più indicato il decontratturante.",
    },
    {
      q: "Si usa l'olio?",
      a: "Sì, è parte essenziale del Lomi Lomi: olio caldo, applicato in modo generoso. Vieni con abiti comodi, scuri se possibile.",
    },
    {
      q: "Va bene per una prima esperienza?",
      a: "Sì. Il ritmo è lento e continuo, l'effetto è di grande calma. Al telefono capiamo insieme se è il massaggio giusto per te.",
    },
  ],
  relatedServices: [
    "massaggio-californiano",
    "massaggio-ayurvedico",
    "aromaterapia",
  ],
  relatedArticles: ["prima-volta-studio-olistico"],
};

export const aromaterapiaContent: IServicePageContent = {
  slug: "aromaterapia",
  displayName: "Aromaterapia",
  title: "Aromaterapia a Firenze · Massaggio con Olii Essenziali",
  description:
    "Aromaterapia a Firenze: massaggio con olii essenziali naturali. Tatto e olfatto insieme, per armonia fisica, mentale ed emotiva. 60 minuti con Regina a Sesto Fiorentino.",
  keywords:
    "aromaterapia firenze, massaggio olii essenziali firenze, trattamento aromaterapico sesto fiorentino, olii essenziali benessere firenze",
  serviceType: "Aromatherapy",
  durationISO: "PT60M",
  durationHuman: "60 minuti",
  heroImage: "/heroes/aromaterapia.jpg",
  heroAlt:
    "Aromaterapia con olii essenziali nello studio olistico Laviens",
  eyebrow: "Massaggi con olii",
  heroTitle: "Aromaterapia.",
  heroSubtitle: "Tatto e olfatto, insieme — due sensi primordiali.",
  lead: (
    <>
      L'<em>aromaterapia</em> unisce il tocco del massaggio agli olii
      essenziali naturali. Due sensi primordiali — tatto e olfatto —
      lavorano insieme per trovare armonia fisica, mentale ed emotiva.
      Gli olii sono scelti per ogni cliente in base allo stato del
      momento.
    </>
  ),
  whoFor: [
    "Chi ha bisogno di lavorare anche sul piano emotivo, non solo muscolare.",
    "Chi cerca un'esperienza sensoriale più ricca di un semplice massaggio.",
    "Chi è in un periodo di ansia, insonnia, stanchezza mentale.",
    "Chi ama gli olii naturali e trova significato nei profumi.",
  ],
  howItWorks: (
    <>
      <p>
        La seduta dura <strong>60 minuti</strong>. All'inizio, scegliamo
        insieme 2–3 olii essenziali in base a come stai: lavanda per la
        calma, rosmarino per risvegliare il corpo, bergamotto per
        l'umore, eucalipto per le vie respiratorie, e così via.
      </p>
      <p>
        La miscela viene diluita in un olio vettore e usata in un
        massaggio lento e continuo. L'effetto non è solo tattile: gli
        olii essenziali, attraverso l'olfatto, agiscono direttamente sul
        sistema limbico — la parte del cervello che regola emozioni e
        memoria.
      </p>
    </>
  ),
  faq: [
    {
      q: "Che olii usate?",
      a: "Solo olii essenziali puri, di qualità terapeutica. Lavanda, camomilla, bergamotto, eucalipto, rosmarino, ylang-ylang, pino silvestre sono i più comuni. Scegliamo insieme in base al tuo momento.",
    },
    {
      q: "Ho la pelle sensibile, posso farlo?",
      a: "Sì, diluiamo sempre gli olii essenziali in un olio vettore neutro. Se hai allergie conosciute, dimmelo in telefonata e scegliamo olii adatti.",
    },
    {
      q: "L'effetto è soprattutto fisico o emotivo?",
      a: "Entrambi, ma spesso emotivo. L'olfatto è collegato direttamente al sistema limbico — gli olii possono fare emergere stati d'animo che non erano consapevoli.",
    },
    {
      q: "Posso portare il mio olio preferito?",
      a: "Volentieri. Dimmelo in telefonata: lo integriamo nella miscela.",
    },
  ],
  relatedServices: [
    "massaggio-ayurvedico",
    "massaggio-svedese",
    "massaggio-californiano",
  ],
  relatedArticles: ["prima-volta-studio-olistico"],
};

export const skinCareContent: IServicePageContent = {
  slug: "skin-care",
  displayName: "Skin Care Viso & Corpo",
  title: "Skin Care Viso & Corpo a Firenze · Trattamenti su Misura",
  description:
    "Skin Care a Firenze: trattamenti viso e corpo personalizzati in base al fototipo. Laviens Studio Olistico a Sesto Fiorentino, con Regina.",
  keywords:
    "skin care firenze, trattamento viso firenze, trattamento corpo firenze, estetista sesto fiorentino, pulizia viso sesto fiorentino",
  serviceType: "Skin Care Treatment",
  durationISO: "PT60M",
  durationHuman: "60 o 90 minuti",
  heroImage: "/heroes/skin-care.jpg",
  heroAlt:
    "Trattamento skin care viso nello studio olistico Laviens",
  eyebrow: "Cura della pelle",
  heroTitle: "Skin care.",
  heroSubtitle: "Su misura per il tuo fototipo, non un protocollo generico.",
  lead: (
    <>
      I trattamenti <em>skin care</em> di Laviens sono protocolli
      personalizzati in base al fototipo, alle esigenze della pelle e alla
      stagione. Pulizia, idratazione, nutrimento, anti-age — niente
      pacchetti rigidi, tutto costruito su misura.
    </>
  ),
  whoFor: [
    "Chi vuole prendersi cura della pelle in modo regolare.",
    "Chi ha esigenze specifiche (pelle disidratata, grassa, sensibile, matura).",
    "Chi preferisce un approccio personalizzato a un protocollo standard.",
    "Chi cerca un trattamento viso autentico, senza upselling.",
  ],
  howItWorks: (
    <>
      <p>
        Prima della seduta, in telefonata, facciamo una breve
        anamnesi: tipo di pelle, prodotti che usi, eventuali problemi
        specifici. In studio, il trattamento è costruito intorno a te:
        pulizia profonda, esfoliazione leggera, maschera specifica,
        idratazione finale.
      </p>
      <p>
        Per i trattamenti corpo, il protocollo varia in base
        all'obiettivo: idratazione, tonificazione, scrub esfoliante,
        nutrimento dopo stagioni difficili.
      </p>
    </>
  ),
  faq: [
    {
      q: "Che prodotti usate?",
      a: "Prodotti professionali di alta qualità, scelti in base al fototipo e alle esigenze. Privilegiamo linee naturali e rispettose della pelle.",
    },
    {
      q: "Fate anche epilazione?",
      a: "Sì. Epilazione a cera calda su braccia (€25), schiena (€30), gamba completa (€40) e brasiliana donna (€50) o uomo (€120). Tariffe e durate aggiornate nel catalogo trattamenti.",
    },
    {
      q: "Quanto spesso?",
      a: "Un trattamento viso ogni 4–6 settimane è un buon ritmo di mantenimento. Per problemi specifici, possiamo consigliare cicli più ravvicinati.",
    },
    {
      q: "Posso abbinarlo a un massaggio?",
      a: "Sì. Molti clienti scelgono un trattamento combinato — massaggio decontratturante + pulizia viso, per esempio. Ci organizziamo.",
    },
  ],
  relatedServices: [
    "aromaterapia",
    "massaggio-svedese",
    "massaggio-californiano",
  ],
  relatedArticles: ["prima-volta-studio-olistico"],
};

export const brasilianaContent: IServicePageContent = {
  slug: "epilazione-brasiliana",
  displayName: "Epilazione Brasiliana",
  title: "Ceretta Brasiliana a Firenze · Donna e Uomo · Laviens",
  description:
    "Epilazione brasiliana a Firenze con cera calda: donna €50 (45 min), uomo €120 (1 ora 30). Studio privato a Sesto Fiorentino, un appuntamento alla volta.",
  keywords:
    "ceretta brasiliana firenze, epilazione brasiliana firenze, ceretta brasiliana uomo firenze, epilazione intima firenze, ceretta brasiliana sesto fiorentino",
  serviceType: "Brazilian Waxing",
  durationISO: "PT45M",
  durationHuman: "45 min (donna) · 1 ora 30 (uomo)",
  heroImage: "/heroes/epilazione-brasiliana.jpg",
  heroAlt:
    "Epilazione brasiliana con cera calda nello studio privato Laviens a Sesto Fiorentino",
  eyebrow: "Epilazione a cera",
  heroTitle: "Brasiliana.",
  heroSubtitle:
    "L'epilazione intima con cera calda — tecnica autentica, massima discrezione.",
  lead: (
    <>
      L'<em>epilazione brasiliana</em> è la ceretta intima integrale,
      eseguita con cera calda. Regina è brasiliana e la esegue con la
      tecnica autentica del suo paese. Il servizio è per ambosessi —
      donna €50 (45 minuti), uomo €120 (1 ora e 30) — sempre in uno
      studio privato, un appuntamento alla volta.
    </>
  ),
  whoFor: [
    "Chi cerca un'epilazione intima completa, eseguita con cura e senza fretta.",
    "Chi tiene alla discrezione: studio privato, nessuna sala d'attesa, nessun incrocio con altri clienti.",
    "Chi passa dal rasoio alla cera e vuole un risultato che dura settimane.",
    "Chi cerca un appuntamento dedicato per l'epilazione intima maschile.",
  ],
  howItWorks: (
    <>
      <p>
        L'appuntamento donna dura <strong>45 minuti</strong> (€50);
        quello uomo <strong>1 ora e 30</strong> (€120), perché la
        superficie e la densità del pelo richiedono più tempo. Si lavora
        con cera calda, stesa e rimossa per piccole aree, con pause
        quando servono.
      </p>
      <p>
        L'igiene è parte del servizio: materiale monouso e cera mai
        riutilizzata. E la discrezione anche — lo studio è privato, un
        appuntamento alla volta: nessuna sala d'attesa, nessun incrocio
        con altri clienti.
      </p>
      <p>
        Per le altre zone — braccia, schiena, gambe — c'è la pagina{" "}
        <Link to="/epilazione-cera-calda">epilazione a cera calda</Link>.
        E per arrivare alla seduta senza dubbi, la nostra{" "}
        <Link to="/journal/depilazione-cera-calda-guida">
          guida alla depilazione a cera calda
        </Link>{" "}
        spiega tutto passo per passo.
      </p>
    </>
  ),
  notFor: [
    "Pelle lesa, irritata o con infezioni in corso nella zona da trattare.",
    "Subito dopo un'esposizione solare intensa: meglio lasciar passare qualche giorno.",
    "Durante trattamenti che assottigliano la pelle (retinoidi, acidi esfolianti): meglio parlarne prima.",
  ],
  faq: [
    {
      q: "Fa male?",
      a: "È un fastidio breve, non un dolore da temere. La cera calda dilata il follicolo e riduce la trazione sulla pelle; con le sedute successive la ricrescita si indebolisce e il fastidio diminuisce.",
    },
    {
      q: "Quanto dura la pelle liscia?",
      a: "In media 3–4 settimane, a seconda della ricrescita individuale. Con la regolarità il pelo tende a tornare più fine.",
    },
    {
      q: "Quanto deve essere lungo il pelo?",
      a: "Circa 4–5 mm, cioè 2–3 settimane di ricrescita dopo il rasoio. Se è più corto, la cera non riesce a catturarlo.",
    },
    {
      q: "La fate davvero anche agli uomini?",
      a: "Sì, con un appuntamento dedicato: 1 ora e 30, €120. Stessa cura, stessa discrezione — studio privato, un appuntamento alla volta.",
    },
    {
      q: "Che differenza c'è con la ceretta classica?",
      a: "La brasiliana è l'epilazione intima integrale, con manualità e tempi dedicati. La ceretta classica riguarda zone come braccia, schiena e gambe — ha una pagina e tariffe proprie.",
    },
  ],
  relatedServices: ["epilazione-cera-calda", "skin-care"],
  relatedArticles: ["depilazione-cera-calda-guida"],
};

export const ceraCaldaContent: IServicePageContent = {
  slug: "epilazione-cera-calda",
  displayName: "Epilazione a Cera Calda",
  title: "Ceretta a Firenze · Epilazione a Cera Calda · Laviens",
  description:
    "Epilazione a cera calda a Firenze: braccia €25, schiena €30, gamba completa €40. Cera delicata, adatta a pelli sensibili. Studio Laviens a Sesto Fiorentino.",
  keywords:
    "ceretta firenze, epilazione cera calda firenze, ceretta sesto fiorentino, ceretta schiena uomo firenze, depilazione firenze",
  serviceType: "Waxing Services",
  durationISO: "PT30M",
  durationHuman: "30–45 min per zona",
  heroImage: "/heroes/epilazione-cera-calda.jpg",
  heroAlt:
    "Epilazione a cera calda nello studio Laviens a Sesto Fiorentino",
  eyebrow: "Epilazione a cera",
  heroTitle: "Cera calda.",
  heroSubtitle:
    "Aderisce al pelo, non alla pelle — per settimane di pelle liscia.",
  lead: (
    <>
      L'<em>epilazione a cera calda</em> toglie il pelo alla radice con
      il minimo stress per la pelle: il calore dilata il follicolo e la
      cera aderisce al pelo, non alla pelle. Da Laviens si lavora per
      zone — braccia, schiena, gambe — con materiale monouso e cera mai
      riutilizzata.
    </>
  ),
  whoFor: [
    "Chi vuole una pelle liscia che dura settimane, non giorni.",
    "Chi ha la pelle sensibile e trova le strisce a freddo troppo aggressive.",
    "Chi cerca una ceretta curata per schiena o braccia, anche al maschile.",
    "Chi preferisce uno studio privato a un salone affollato.",
  ],
  howItWorks: (
    <>
      <p>
        Si lavora per zone, su appuntamento: <strong>braccia €25</strong>{" "}
        (30 min), <strong>schiena €30</strong> (30 min),{" "}
        <strong>gamba completa €40</strong> (30 min). La cera viene stesa
        calda e rimossa per piccole aree; il materiale è monouso e la
        cera non viene mai riutilizzata.
      </p>
      <p>
        Perché la cera calda e non le strisce a freddo? Il calore dilata
        il follicolo e la cera avvolge il pelo senza attaccarsi alla
        pelle: la rimozione è più profonda e la trazione sull'epidermide
        minore. Per questo è più delicata, adatta anche a pelli
        sensibili.
      </p>
      <p>
        Per l'epilazione intima c'è un servizio dedicato, la{" "}
        <Link to="/epilazione-brasiliana">brasiliana</Link> — la
        specialità di Regina. E se è la tua prima ceretta, la{" "}
        <Link to="/journal/depilazione-cera-calda-guida">
          guida pratica
        </Link>{" "}
        del Journal spiega come prepararsi.
      </p>
    </>
  ),
  notFor: [
    "Pelle lesa, irritata o con infezioni in corso nella zona da trattare.",
    "Subito dopo un'esposizione solare intensa: meglio lasciar passare qualche giorno.",
  ],
  faq: [
    {
      q: "Quanto costa la ceretta da Laviens?",
      a: "Braccia €25, schiena €30, gamba completa €40 — circa 30 minuti a zona. L'epilazione intima brasiliana ha tariffe dedicate: donna €50, uomo €120.",
    },
    {
      q: "Perché la cera calda e non le strisce a freddo?",
      a: "La cera calda aderisce al pelo, non alla pelle: il calore dilata il follicolo e il pelo viene rimosso con meno trazione. Meno irritazione, anche su pelli sensibili.",
    },
    {
      q: "Quanto deve essere lungo il pelo?",
      a: "Circa 4–5 mm, cioè 2–3 settimane di ricrescita. Se è troppo corto la cera non lo cattura; se è molto lungo lo accorciamo prima noi.",
    },
    {
      q: "Quanto dura il risultato?",
      a: "In media 3–4 settimane. Con la regolarità la ricrescita tende a diventare più fine e più lenta.",
    },
    {
      q: "Fate la ceretta anche agli uomini?",
      a: "Sì — schiena, braccia e altre zone, su appuntamento. Studio privato, un appuntamento alla volta.",
    },
  ],
  relatedServices: ["epilazione-brasiliana", "skin-care"],
  relatedArticles: ["depilazione-cera-calda-guida"],
};

export const emozionaleContent: IServicePageContent = {
  slug: "massaggio-emozionale",
  displayName: "Massaggio Emozionale",
  title: "Massaggio Emozionale a Firenze · Ascolto Profondo del Corpo",
  description:
    "Massaggio emozionale a Firenze: un ascolto profondo fatto con le mani, per sciogliere le tensioni emotive somatizzate nel corpo. 60 minuti a Sesto Fiorentino.",
  keywords:
    "massaggio emozionale firenze, massaggio rilassante profondo firenze, massaggio emozionale sesto fiorentino, tensioni emotive massaggio",
  serviceType: "Emotional Massage",
  durationISO: "PT60M",
  durationHuman: "60 minuti",
  heroImage: "/heroes/massaggio-emozionale.jpg",
  heroAlt:
    "Massaggio emozionale nello studio olistico Laviens a Sesto Fiorentino",
  eyebrow: "Massaggi emozionali",
  heroTitle: "Emozionale.",
  heroSubtitle: "Un ascolto profondo, fatto con le mani.",
  lead: (
    <>
      Il <em>massaggio emozionale</em> è un ascolto profondo fatto con
      le mani: un tocco lento e presente che lascia emergere e sciogliere
      le tensioni emotive somatizzate nel corpo — spalle che si chiudono,
      respiro corto, stomaco stretto. Non è psicoterapia e non la
      sostituisce: è un massaggio benessere basato sull'ascolto del
      corpo.
    </>
  ),
  whoFor: [
    "Chi attraversa un periodo di carico mentale e sente che il corpo lo trattiene.",
    "Chi si accorge di somatizzare: respiro corto, spalle chiuse, stomaco stretto.",
    "Chi cerca un massaggio in cui l'ascolto conta più della tecnica.",
    "Chi ha provato i massaggi più tecnici senza trovare quello che cercava.",
  ],
  howItWorks: (
    <>
      <p>
        La seduta dura <strong>60 minuti</strong>. Si inizia con qualche
        minuto di conversazione — come stai, cosa senti nel corpo — poi
        si passa al lettino. Il tocco è lento e presente, la pressione
        moderata: non si cercano contratture, si ascolta.
      </p>
      <p>
        Durante il massaggio può emergere qualcosa — un sospiro profondo,
        un momento di commozione. Va bene così: non c'è niente da
        spiegare né da trattenere. Regina accompagna, senza interpretare.
      </p>
      <p>
        È vicino al <Link to="/massaggio-californiano">californiano</Link>{" "}
        per l'effetto avvolgente, ma segue meno uno schema e più quello
        che il corpo comunica in quel momento. Si abbina bene
        all'<Link to="/aromaterapia">aromaterapia</Link>, quando un olio
        essenziale può aiutare a lasciar andare.
      </p>
    </>
  ),
  faq: [
    {
      q: "È una forma di psicoterapia?",
      a: "No. È un massaggio benessere basato sull'ascolto del corpo: non è psicoterapia e non la sostituisce. Se stai seguendo un percorso, può accompagnarlo — non prenderne il posto.",
    },
    {
      q: "Cosa succede durante la seduta?",
      a: "Il tocco è lento e presente, la pressione moderata. Si lavora in silenzio o quasi; a volte emergono emozioni — un sospiro, qualche lacrima — ed è normale, non serve spiegare nulla.",
    },
    {
      q: "In cosa è diverso dal californiano?",
      a: "Sono vicini. Il californiano segue un metodo preciso di manovre avvolgenti; l'emozionale segue meno uno schema e più quello che il corpo comunica in quel momento.",
    },
    {
      q: "Quanto dura e ogni quanto farlo?",
      a: "60 minuti. Non c'è una cadenza fissa: c'è chi lo sceglie nei momenti di carico e chi lo inserisce in un ciclo insieme ad altri trattamenti.",
    },
  ],
  relatedServices: [
    "massaggio-californiano",
    "massaggio-svedese",
    "aromaterapia",
  ],
  relatedArticles: ["prima-volta-studio-olistico"],
};

export const anticelluliteContent: IServicePageContent = {
  slug: "massaggio-anticellulite",
  displayName: "Massaggio Anticellulite",
  title: "Massaggio Anticellulite a Firenze · Modellante e Tonificante",
  description:
    "Massaggio anticellulite a Firenze: modellante e tonificante, stimola il microcircolo per ridurre gli inestetismi della cellulite. 60 minuti a Sesto Fiorentino.",
  keywords:
    "massaggio anticellulite firenze, massaggio modellante firenze, massaggio tonificante firenze, massaggio anticellulite sesto fiorentino",
  serviceType: "Anti-Cellulite Massage",
  durationISO: "PT60M",
  durationHuman: "60 minuti",
  heroImage: "/heroes/massaggio-anticellulite.jpg",
  heroAlt:
    "Massaggio anticellulite modellante nello studio olistico Laviens",
  eyebrow: "Massaggi specifici",
  heroTitle: "Anticellulite.",
  heroSubtitle: "Modellante e tonificante — con una premessa onesta: serve costanza.",
  lead: (
    <>
      Il <em>massaggio anticellulite</em> è un lavoro modellante e
      tonificante: manovre decise che stimolano il microcircolo nelle
      zone dove la cellulite si concentra. Una premessa onesta: la
      cellulite non sparisce con una seduta. Un ciclo di sedute
      ravvicinate riduce gli inestetismi — non li elimina.
    </>
  ),
  whoFor: [
    "Chi vuole lavorare sugli inestetismi della cellulite con costanza, non con promesse.",
    "Chi cerca un massaggio modellante e tonificante su gambe, glutei e fianchi.",
    "Chi vuole affiancare al massaggio movimento e idratazione, per un risultato che si vede.",
    "Chi preferisce un lavoro manuale ai macchinari.",
  ],
  howItWorks: (
    <>
      <p>
        La seduta dura <strong>60 minuti</strong>. Si lavora sulle zone
        dove la cellulite si concentra — cosce, glutei, fianchi — con
        manovre più decise di un massaggio rilassante: impastamenti,
        sollevamenti, frizioni che stimolano il microcircolo e i
        tessuti.
      </p>
      <p>
        Non è il <Link to="/massaggio-linfodrenante">linfodrenante</Link>:
        quello lavora con pressione leggera sul sistema linfatico per
        drenare i liquidi, l'anticellulite lavora più in profondità su
        tessuto e microcircolo. In alcuni percorsi si alternano.
      </p>
      <p>
        Per un risultato visibile serve costanza: un ciclo di sedute
        ravvicinate (in genere una a settimana), abbinato a movimento e
        idratazione. Una seduta singola è un buon punto di partenza, non
        una soluzione.
      </p>
    </>
  ),
  notFor: [
    "Gravidanza: meglio parlarne prima e valutare insieme un trattamento più adatto.",
    "Capillari molto fragili o patologie venose in corso, senza il parere del medico.",
    "Infiammazioni acute o lesioni della pelle nelle zone da trattare.",
  ],
  faq: [
    {
      q: "La cellulite sparisce?",
      a: "No, e chi lo promette non è onesto. Un ciclo di sedute ravvicinate riduce visibilmente gli inestetismi e migliora la qualità dei tessuti — non li elimina.",
    },
    {
      q: "Quante sedute servono?",
      a: "Dipende dal punto di partenza. In genere un ciclo di 6–10 sedute ravvicinate (una a settimana), poi mantenimento. Ne parliamo con realismo prima di iniziare.",
    },
    {
      q: "È doloroso?",
      a: "Le manovre sono più decise di un massaggio rilassante e su alcune zone si sentono. La pressione si calibra sempre sul tuo feedback, mai oltre.",
    },
    {
      q: "Anticellulite o linfodrenante?",
      a: "Anticellulite se l'obiettivo è modellare e tonificare i tessuti. Linfodrenante se il tema è ritenzione idrica e gambe pesanti. In alcuni percorsi si alternano.",
    },
  ],
  relatedServices: ["massaggio-linfodrenante", "skin-care"],
  relatedArticles: [],
};

export const SERVICES: Record<string, IServicePageContent> = {
  "massaggio-svedese": svedeseContent,
  "massaggio-decontratturante": decontratturanteContent,
  "massaggio-linfodrenante": linfodrenanteContent,
  "massaggio-ayurvedico": ayurvedicoContent,
  "massaggio-californiano": californianoContent,
  "massaggio-hawaiano": hawaianoContent,
  aromaterapia: aromaterapiaContent,
  "skin-care": skinCareContent,
  "epilazione-brasiliana": brasilianaContent,
  "epilazione-cera-calda": ceraCaldaContent,
  "massaggio-emozionale": emozionaleContent,
  "massaggio-anticellulite": anticelluliteContent,
};

export const SERVICE_LIST: IServicePageContent[] = Object.values(SERVICES);
